import { useState, useEffect, useRef } from "react";
import Modal from "../../../components/Modal";
import Layout from "../../../components/Layout";
import { Grid } from "@mui/material";
import { toast } from "react-toastify";
// import BulkEmployee from "../../../components/BulkEmployeeCard";
import BreadCrumb from "../../../components/BreadCrumb";
import useWindowSize from "../../../hooks/useWindow";
import Table from "../../../components/Table/Table";
import { httprequest } from "../../../data/api";
import Select from "react-select";
import PageLoader from "../../../components/PageLoader";
// import SelectGroup from "../../../components/FormComponent/SelectGroup";
import { TableSearch } from "../../../components/Table/TableActions";
import Papa from "papaparse";
import { Icon } from "@iconify/react";
import Button from "../../../components/Button";
import GuardianCSV from "../../../assets/sample/guardian.csv";
import { useNavigate } from "react-router-dom";
import { useUserState } from "../../../context/UserContext";
import useStyles from "../styles";

const STUDENT_ENDPOINT = "/api/v1/students";
const BATCH_GUARDIAN = "/api/v1/";

const BatchImportGuardian = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const { profile } = useUserState();
  const [bulkModal, setBulkModal] = useState(false);
  const [ispageFetching, setIsPageFetching] = useState(false);
  const [batchCreating, setBatchCreating] = useState(false);
  const [searchResult, setSearchResult] = useState([]);
  const [studentData, setStudentsData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  // const [selectedStudents, setSelectedStudents] = useState({});
  const fileInputRef = useRef();
  const width = useWindowSize();
  const mobile = width < 600;

  /**
   * Handles the file input change event to set the selected CSV file.
   */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setSelectedFile(file);
    } else {
      toast.error("Please upload a valid .csv file");
    }
  };

  /**
   * Removes the selected file from the state and resets the file <input type="text" className="" />
   */
  const handleRemoveFile = () => {
    setSelectedFile(null);
    fileInputRef.current.value = "";
  };

  /**
 * Toggles the visibility of the bulk creation modal.
 *
 * @function handleCreateBulkModal
 */
  const handleCreateBulkModal = () => {
    setBulkModal(true);
  };

  const handleCloseBulkModal = () => {
    setBulkModal(false);
  };

  /**
   * Handles the batch creation of guardians by sending the parsed data to the server.
   * It cleans the data by removing the `temporal_id` field before sending it.
   */
  const handleBatchCreate = async () => {
    setBatchCreating(true);

    try {
      const cleanedData = searchResult.map((item) => {
        const copy = { ...item };
        delete copy.temporal_id;

        return copy;
      });

      const res = await httprequest.post(
        `${BATCH_GUARDIAN}${profile?.school_id}/guardians/batch`,
        { guardians: cleanedData }
      );

      if (res?.data?.message) {
        toast.success(res.data.message);
        setTimeout(() => {
          navigate("/users/guardians");
        }, 3000);
        setSearchResult([]);
      } else {
        toast.error("Unexpected response format from server");
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "An unknown error occurred";
      toast.error(errorMessage);
    } finally {
      setBatchCreating(false);
    }
  };

  /**
   *
   * @returns - Parses the selected CSV file and maps the data to the required format.
   * If no file is selected, it shows an error message.
   */
  const handleMapBatchGuardian = () => {
    if (!selectedFile) {
      toast.error("Please select a CSV file to upload");

      return;
    }

    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete(results) {
        const parsedData = results.data.map((entry, ind) => ({
          temporal_id: ind,
          first_name: entry.first_name?.trim(),
          last_name: entry.last_name?.trim(),
          email: entry.email?.trim(),
          gender: entry.gender?.trim().charAt(0).toUpperCase() + entry.gender?.trim().slice(1).toLowerCase(),
          address: entry.address?.trim(),
          contact: entry.contact?.toString().trim()
        }));
        setSearchResult(parsedData);
        setBulkModal(false);
      },
      error(err) {
        toast.error("CSV Parse Error:", err);
      }
    });
  };

  /**
   * Handles the change event for the student selection dropdown.
   * @param {*} param0
   */
  const handleSingleWardChange = ({ selectedOption, row }) => {
    if (!selectedOption) return;

    const selectedId = selectedOption?.value;

    // Check if this student is already assigned as a primary to another guardian
    const duplicateEntry = searchResult.find(
      (entry) => entry.temporal_id !== row.temporal_id &&
      entry.assigned_students?.primary === selectedId
    );

    if (duplicateEntry) {
      toast.error(
        `${selectedOption?.label || "This student"} has already been assigned as a primary ward.`
      );

      return;
    }

    // Update the main searchResult state
    setSearchResult((prev) => prev.map((entry) => entry.temporal_id === row.temporal_id
      ? {
        ...entry,
        assigned_students: {
          ...entry.assigned_students,
          primary: selectedId
        }
      }
      : entry
    )
    );
  };

  /**
   * This function handles the selection of a role for a guardian in the batch import process.
   * It updates the `searchResult` state with the selected role for the specific guardian row
   * @param {*} e
   * @param {*} rowData
   */
  const handleSetSecondary = ({ selectedOption, row }) => {
    if (!selectedOption || selectedOption?.length === 0) return;

    const selectedIds = selectedOption?.map((opt) => opt?.value);

    // 1. Enforce max 2 secondaries selected per guardian
    if (selectedIds.length > 2) {
      toast.error("You can only assign up to 2 secondary students.");

      return;
    }

    // 2. Get this guardian's current primary
    const currentPrimaryId = row?.assigned_students?.primary;

    // 3. Prevent assigning the same student as both primary and secondary to the same guardian
    const conflictingId = selectedIds?.find((id) => id === currentPrimaryId);

    if (conflictingId) {
      const conflictStudent = selectedOption?.find(
        (opt) => opt?.value === conflictingId
      );

      toast.error(
        `${conflictStudent?.label || "This student"} is already assigned as a primary to this guardian and cannot also be a secondary.`
      );

      return;
    }

    // 4. Prevent assigning a student as secondary to more than 2 different guardians
    for (const id of selectedIds) {
      const secondaryCount = searchResult.filter(
        (entry) => entry?.temporal_id !== row?.temporal_id &&
        entry.assigned_students?.secondary?.includes(id)
      ).length;

      if (secondaryCount >= 2) {
        const student = selectedOption.find((opt) => opt?.value === id);
        toast.error(
          `${student?.label || "This student"} has already been assigned as a secondary to 2 other guardians.`
        );

        return;
      }
    }

    // 6. Update the main state with selected secondary students
    setSearchResult((prev) => prev.map((entry) => entry.temporal_id === row.temporal_id
      ? {
        ...entry,
        assigned_students: {
          ...entry.assigned_students,
          secondary: selectedIds
        }
      }
      : entry
    )
    );
  };

  /**
   * Fetches all students from the API and sets the state with the student data.
   * It formats the student data to be used in a select dropdown.
   */
  const getAllStudents = () => {
    setIsPageFetching(true);
    httprequest.get(STUDENT_ENDPOINT).then((res) => {
      setIsPageFetching(false);
      // toast.success(res?.data?.message);
      // setStudentsData(res?.data?.data?.filter((activeStudent) => activeStudent?.is_active === true));
      // setSearchResult(res?.data?.data?.filter((activeStudent) => activeStudent?.is_active === true));
      setStudentsData(res?.data?.data?.map((stdn) => {
        return {
          label: `${stdn?.student?.last_name} ${stdn?.student?.first_name}`,
          value: stdn?.student?.id
        };
      }));
    }).catch((err) => {
      setIsPageFetching(false);
      toast.error(err?.response?.data?.message);
    });
  };

  // Define the columns for the notice table
  const columns = [
    {
      name: "Guardian Name",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Guardian Name</p>}
          {`${row?.last_name || "--/--"} ${row?.first_name || "--/--"}`}
        </>
      )
    },
    {
      name: "Gender",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Gender</p>}
          {row?.gender || "--/--"}
        </>
      )
    },
    {
      name: "Email",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Email</p>}
          {row?.email || "--/--"}
        </>
      )
    },
    {
      name: "Set Primary",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Set Primary</p>}
          <div className="single-select">
            <Select
              value={
                studentData.find(
                  (opt) => opt.value === row?.assigned_students?.primary
                ) || null
              }
              name="student"
              options={studentData}
              onChange={(selectedOption) => {
                handleSingleWardChange({
                  selectedOption,
                  row
                });
              }}
              placeholder="Select a student"
              isClearable
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 })
              }}
            />
          </div>
        </>
      )
    },
    {
      name: "Set Secondary",
      cell: (row) => (
        <>
          {mobile && <p className="mobile-styling">Set Secondary</p>}
          <div className="single-select">
            <Select
              value={
                studentData.filter((opt) => row?.assigned_students?.secondary?.includes(opt.value)
                ) || []
              }
              name="student"
              options={studentData}
              onChange={(selectedOption) => {
                handleSetSecondary({
                  selectedOption,
                  row
                });
              }}
              placeholder="Select a student"
              isMulti
              isClearable
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 })
              }}
            />
          </div>
        </>
      )
    }

  ];
  // --| Filter Notice table using title, description, priority who can access
  const handleSearchBatchGuardian = (e) => {
    const { value } = e.target;

    const valArray = value.split(" ");
    // --| Filter data by partial match onchange in the search input box
    const result = searchResult?.filter((data) => valArray?.every(
      (word) => data?.first_name?.toLowerCase().includes(word.toLowerCase()) ||
              data?.last_name?.toLowerCase().includes(word.toLowerCase()) ||
              data?.email?.toLowerCase().includes(word.toLowerCase()) ||
              data?.gender?.toLowerCase().includes(word.toLowerCase())
    )
    );
    setSearchResult(result);
  };

  useEffect(() => {
    getAllStudents();
  }, []);

  return (
    <Layout children={
      <div>
        <div className="breadcrumb-container">
          <div>
            <BreadCrumb
              link_one="/users/guardians"
              link_one_name="Guardians"
              active="Batch Import Guardian"
              description="Batch import guardian"
            />
          </div>
          <div>
            <Button
              variant="primary"
              buttonSize="fluid"
              color="btndefault"
              onClick={handleCreateBulkModal}
              buttonName="Upload CSV"
            />
          </div>
        </div>
        <div className="page-content full-page">
          {ispageFetching ? (
            <PageLoader />
          ) : (
            <Table
              data={searchResult}
              columns={columns}
              subHeader={true}
              pagination
              //   isScrollable={true}
              subHeaderComponent={
                <div className="action_div">
                  <TableSearch
                    disabled
                    placeholder="Search Student..."
                    searchTableFunc={handleSearchBatchGuardian}
                  />
                  {/* <div>
                    <SelectGroup
                      children={
                        <select onChange={handleFilterChange}>
                          <option value="">---Select---</option>
                          {classesData?.map((data) => {
                            return (
                              <option key={data?.id} value={data?.id}>
                                {data?.class_name}
                              </option>
                            );
                          })}
                        </select>
                      }
                    />
                  </div> */}
                </div>}
            />
          )}
          {selectedFile && (
            <div className={classes.batchbtn}>
              <Button onClick={() => {
                navigate("/users/guardians");
              }} variant="primaryoutline" buttonSize="medium" color="btnfontprimary" buttonName="Back"
              />
              <Button isLoading={batchCreating} onClick={handleBatchCreate} variant="primary" buttonSize="medium" color="btndefault" buttonName="Create Guardians" />
            </div>
          )}
          {/* <ToastContainer /> */}
        </div>
        {/* CREATE BULK EMPLOYEE */}
        <Modal
          title="Create Batch Guardian"
          subtitle={
            <div>Create multiple Guardian at the same time by importing a csv file.
            </div>}
          modalContent={
            <>
              <Grid container spacing={2} className="input-padding">
                <Grid item lg={10} md={12} sm={12} xs={12}>
                  <p className="key-pair-name">1. Download sample .csv file here.</p>
                  <p className="key-pair-name">2. Fill in all columns. All fields are mandatory.</p>
                  {/* <p className="key-pair-name">3. Allowed staff types are academic teaching staffs.</p> */}
                  <p className="key-pair-name">3. Do not include extra columns nor rename columns in the document.</p>
                  <p className="key-pair-name">4. Upload the completed excel document</p>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <div>
                    <div className={classes.container}>
                      <input
                        type="file"
                        accept=".csv"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                      />
                      <button className={classes.uploadButton} onClick={() => fileInputRef.current.click()}>
          Upload CSV
                      </button>
                      <a href={GuardianCSV} download className={classes.downloadText}>
          Download a sample csv file
                      </a>
                    </div>

                    {selectedFile && (
                      <div className={classes.selectedFile}>
                        <p>
            Selected file: <strong>{selectedFile.name}</strong>
                        </p>
                        <Icon
                          icon="line-md:minus-circle"
                          width="25"
                          height="25"
                          style={{ color: "#D42620", cursor: "pointer" }}
                          onClick={handleRemoveFile}
                        />
                      </div>
                    )}
                  </div>
                  <div className="margin-top-16">
                    <Button
                      variant="primary"
                      buttonSize="full"
                      color="btndefault"
                      onClick={handleMapBatchGuardian}
                      buttonName="View uploaded Guardian"
                    />
                  </div>
                </Grid>
              </Grid>
            </>
          }
          modalFooter={
            <>
            </>
          }
          isopen={bulkModal}
          closeModal={handleCloseBulkModal}
        />
      </div>
    }
    />
  );
};

export default BatchImportGuardian;
